import React, { useEffect, useState, useContext, useCallback } from "react";
import { AuthContext } from "utils/AuthContext";
import { FlexLeftCol } from "styles/global-style";
import Input from "components/Input";
import Button from "components/Button";
import Modal from "components/Modal";
import { getDefaultProjectData } from "utils/DefaultProjectData";
import trashcan from "imgs/trashcan.png";

export default function Disciplines() {
  const { supabase, user, refresh } = useContext(AuthContext);
  const [disciplines, setDisciplines] = useState([]);
  const [editingDiscipline, setEditingDiscipline] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("#9b59b6");

  // Load disciplines from settings or use defaults
  const loadDisciplines = useCallback(async () => {
    if (!supabase || !user?.proj?.id) return;

    // Get project type
    const projectType = user.proj.type;
    if (!projectType) {
      setDisciplines([]);
      return;
    }

    // Get default disciplines (with DoDs, but we'll strip DoDs for display)
    const defaultDisciplines = getDefaultProjectData(projectType).map(({ doDs, ...discipline }) => discipline);

    // Try to load custom disciplines from settings
    const { data: setting } = await supabase
      .from("settings")
      .select("value")
      .eq("project_id", user.proj.id)
      .eq("name", "project_disciplines")
      .maybeSingle();

    if (setting?.value) {
      try {
        const customDisciplines = JSON.parse(setting.value);
        // If custom disciplines exist, use them directly (they represent the current state after deletions)
        // Only merge with defaults for missing properties, but don't add back deleted defaults
        const defaultMap = new Map(defaultDisciplines.map(d => [d.id, d]));
        
        // Start with custom disciplines, fill in missing properties from defaults
        const merged = customDisciplines.map(customDiscipline => {
          const defaultDiscipline = defaultMap.get(customDiscipline.id);
          return defaultDiscipline ? { ...defaultDiscipline, ...customDiscipline } : customDiscipline;
        });
        
        // Sort by order
        merged.sort((a, b) => (a.order || 0) - (b.order || 0));
        setDisciplines(merged);
      } catch (e) {
        console.error("Failed to parse project_disciplines setting:", e);
        setDisciplines(defaultDisciplines);
      }
    } else {
      setDisciplines(defaultDisciplines);
    }
  }, [supabase, user]);

  useEffect(() => {
    loadDisciplines();
  }, [loadDisciplines]);

  // Debug: log disciplines
  useEffect(() => {
    console.log("=== DISCIPLINES PAGE DEBUG ===");
    console.log("Disciplines list:", disciplines.map(d => d.name));
    console.log("Full disciplines:", disciplines);
  }, [disciplines]);


  // Save disciplines to settings
  // Only saves if there are actual customizations, not just defaults
  const saveDisciplines = useCallback(async (newDisciplines) => {
    if (!supabase || !user?.proj?.id) return;

    const projectType = user.proj.type;
    const defaultDisciplines = getDefaultProjectData(projectType);
    const defaultMap = new Map(defaultDisciplines.map(d => [d.id, d]));

    // Get current discipline IDs before updating
    const currentDisciplineIds = new Set(disciplines.map(d => d.id));
    const newDisciplineIds = new Set(newDisciplines.map(d => d.id));
    
    // Find removed discipline IDs
    const removedDisciplineIds = [...currentDisciplineIds].filter(id => !newDisciplineIds.has(id));

    // Update order for all disciplines
    const orderedDisciplines = newDisciplines.map((discipline, index) => ({
      ...discipline,
      order: index
    }));

    // Check if result is just defaults (no customizations)
    // For disciplines page, we only care about discipline properties (not DoDs)
    const hasCustomizations = orderedDisciplines.some(discipline => {
      const defaultDiscipline = defaultMap.get(discipline.id);
      if (!defaultDiscipline) return true; // Custom discipline not in defaults
      
      // Check if discipline properties differ (ignore doDs for this check)
      return discipline.name !== defaultDiscipline.name ||
             discipline.color !== defaultDiscipline.color ||
             discipline.order !== defaultDiscipline.order;
    }) || orderedDisciplines.length !== defaultDisciplines.length;

    if (hasCustomizations) {
      // Strip doDs when saving from disciplines page (they're managed in DoDs page)
      const toStore = orderedDisciplines.map(({ doDs, ...discipline }) => discipline);

      // First, delete ALL existing project_disciplines rows for this project
      await supabase
        .from("settings")
        .delete()
        .eq("project_id", user.proj.id)
        .eq("name", "project_disciplines");

      // Then insert a single row
      await supabase
        .from("settings")
        .insert({
          name: "project_disciplines",
          value: JSON.stringify(toStore),
          project_id: user.proj.id
        });
    } else {
      // Result is just defaults, so delete the setting
      await supabase
        .from("settings")
        .delete()
        .eq("project_id", user.proj.id)
        .eq("name", "project_disciplines");
    }

    // Sync discipline changes with people and work tables
    if (removedDisciplineIds.length > 0) {
      // Remove discipline_id from people table where it matches removed disciplines
      // Only update people who are associated with this project (have days entries)
      const { data: projectData } = await supabase
        .from("projects")
        .select("org_id")
        .eq("id", user.proj.id)
        .single();

      if (projectData?.org_id) {
        // Get people IDs who have days entries for this project
        const { data: daysData } = await supabase
          .from("days")
          .select("person_id")
          .eq("project_id", user.proj.id)
          .not("person_id", "is", null);

        if (daysData && daysData.length > 0) {
          const personIds = [...new Set(daysData.map(d => d.person_id))];
          
          // Update people: set discipline_id to NULL for removed disciplines
          // Only for people associated with this project
          for (const removedId of removedDisciplineIds) {
            await supabase
              .from("people")
              .update({ discipline_id: null })
              .in("id", personIds)
              .eq("discipline_id", removedId);
          }
        }
      }

      // Remove discipline references from work.dod_status JSON
      // Get all work items for this project that have dod_status
      const { data: workItems } = await supabase
        .from("work")
        .select("id, dod_status")
        .eq("project_id", user.proj.id)
        .not("dod_status", "is", null);

      if (workItems && workItems.length > 0) {
        for (const workItem of workItems) {
          if (!workItem.dod_status) continue;

          try {
            const dodStatuses = JSON.parse(workItem.dod_status);
            let updated = false;
            const cleanedStatuses = { ...dodStatuses };

            // Remove discipline IDs from the main object
            for (const removedId of removedDisciplineIds) {
              if (cleanedStatuses[removedId] !== undefined) {
                delete cleanedStatuses[removedId];
                updated = true;
              }
            }

            // Remove discipline IDs from _hidden array
            if (cleanedStatuses._hidden && Array.isArray(cleanedStatuses._hidden)) {
              const originalLength = cleanedStatuses._hidden.length;
              cleanedStatuses._hidden = cleanedStatuses._hidden.filter(
                id => !removedDisciplineIds.includes(id)
              );
              if (cleanedStatuses._hidden.length !== originalLength) {
                updated = true;
              }
              // Remove _hidden if it's empty
              if (cleanedStatuses._hidden.length === 0) {
                delete cleanedStatuses._hidden;
              }
            }

            // Update work item if changes were made
            if (updated) {
              const dodStatusJson = Object.keys(cleanedStatuses).length > 0
                ? JSON.stringify(cleanedStatuses)
                : null;
              
              await supabase
                .from("work")
                .update({ dod_status: dodStatusJson })
                .eq("id", workItem.id);
            }
          } catch (e) {
            console.error(`Failed to parse dod_status for work item ${workItem.id}:`, e);
          }
        }
      }
    }
    
    setDisciplines(orderedDisciplines);
    await refresh();
  }, [supabase, user, refresh, disciplines]);

  // Start editing
  const startEdit = useCallback((discipline) => {
    setEditingDiscipline(discipline);
    setEditName(discipline.name);
    setEditColor(discipline.color);
  }, []);

  // Start adding
  const startAdd = useCallback(() => {
    setIsAdding(true);
    setEditName("");
    setEditColor("#9b59b6");
  }, []);

  // Save edit/add
  const saveEdit = useCallback(async () => {
    if (!editName.trim()) {
      alert("Name is required");
      return;
    }

    const newDiscipline = {
      id: editingDiscipline ? editingDiscipline.id : `custom_${Date.now()}`,
      name: editName.trim(),
      color: editColor,
      order: editingDiscipline ? editingDiscipline.order : disciplines.length
    };

    let updated;
    if (editingDiscipline) {
      // Update existing
      updated = disciplines.map(d => d.id === editingDiscipline.id ? newDiscipline : d);
    } else {
      // Add new
      updated = [...disciplines, newDiscipline];
    }

    setDisciplines(updated);
    await saveDisciplines(updated);
    setEditingDiscipline(null);
    setIsAdding(false);
  }, [editingDiscipline, editName, editColor, disciplines, saveDisciplines]);

  // Delete discipline
  const deleteDiscipline = useCallback(async (discipline) => {
    if (!window.confirm(`Are you sure you want to delete "${discipline.name}"?`)) return;

    const updated = disciplines.filter(d => d.id !== discipline.id).map((d, index) => ({
      ...d,
      order: index
    }));

    setDisciplines(updated);
    await saveDisciplines(updated);
  }, [disciplines, saveDisciplines]);

  return (
    <FlexLeftCol>
      <h2>Disciplines</h2>
      <p style={{ color: "#666", marginBottom: 20 }}>
        Manage disciplines for this project. Click a discipline to edit it.
      </p>

      <div style={{ marginBottom: 20 }}>
        <Button text="Add Discipline" callback={startAdd} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {disciplines.map((discipline) => (
          <div
            key={discipline.id}
            onClick={() => startEdit(discipline)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "8px 16px",
              background: discipline.color,
              color: "white",
              borderRadius: 5,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
              userSelect: "none",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            <span>{discipline.name}</span>
            <img
              alt="delete"
              src={trashcan}
              onClick={(e) => {
                e.stopPropagation();
                deleteDiscipline(discipline);
              }}
              style={{
                height: 14,
                width: 14,
                opacity: 0.9,
                cursor: "pointer",
                filter: "brightness(0) invert(1)",
              }}
            />
          </div>
        ))}
      </div>

      {/* Edit/Add Modal */}
      {(editingDiscipline || isAdding) && (
        <Modal
          title={editingDiscipline ? "Edit Discipline" : "Add Discipline"}
          closeCb={() => {
            setEditingDiscipline(null);
            setIsAdding(false);
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 400 }}>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Discipline Name"
              autoFocus
              left
              style={{ fontWeight: "normal", textAlign: "left" }}
            />
            <div>
              <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: "bold" }}>Color</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input
                  type="color"
                  value={editColor}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    setEditColor(newColor);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    width: 60,
                    height: 40,
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    cursor: "pointer",
                    padding: 0,
                    backgroundColor: editColor
                  }}
                />
                <div
                  style={{
                    padding: "8px 16px",
                    background: editColor,
                    color: "white",
                    borderRadius: 5,
                    fontSize: 14,
                    fontWeight: 500,
                    minWidth: 100,
                    textAlign: "center"
                  }}
                >
                  {editName || "Preview"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 8, justifyContent: "flex-end" }}>
              <button
                style={{ padding: "8px 18px", borderRadius: 5, border: "none", background: "#27ae60", color: "white", fontWeight: "bold", cursor: "pointer" }}
                onClick={saveEdit}
              >
                Save
              </button>
              <button
                style={{ padding: "8px 18px", borderRadius: 5, border: "none", background: "#bbb", color: "white", fontWeight: "bold", cursor: "pointer" }}
                onClick={() => {
                  setEditingDiscipline(null);
                  setIsAdding(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </FlexLeftCol>
  );
}

